import { ErrorReporterContext } from '@sleepstation/components-context'
import PropTypes from 'prop-types'
import { isNotNil } from 'ramda-adjunct'
import React, { useContext, useEffect } from 'react'
import { useLocation } from 'react-use'
import { useQuery } from 'urql'
import UserContext from '../../../context/UserContext'
import { CurrentUserAndEnrolment } from '../../../graphql/documents'
import useDisplayError from '../../../hooks/useDisplayError'
import PageMonitorService from '../../../services/PageMonitorService'
import { redirectToLoginURL } from '../../../utils/auth'
import LoadOverlay from '../../shared/LoadOverlay'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const ERROR_MESSAGE_NO_CURRENT_USER = `You are not permitted to access this page`

const ERROR_MESSAGE_FETCH = `Something has gone wrong`

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const displayNoCurrentUserError = (displayError) => {
  displayError(
    ERROR_MESSAGE_NO_CURRENT_USER,
    new Error(`No session for current user`),
    { buttonCallback: redirectToLoginURL, buttonLabel: `Login` }
  )
  return null
}

const displayFetchError = (displayError, error) => {
  displayError(ERROR_MESSAGE_FETCH, error)
  return null
}

const hasDataWithNoUser = (data) => isNotNil(data) && !data.currentUser

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const App = ({ children }) => {
  // Context
  // ---------------------------------------------------------------------------
  const { displayError } = useDisplayError()
  const errorReporter = useContext(ErrorReporterContext)
  const location = useLocation()

  // Data
  // ---------------------------------------------------------------------------
  const [result] = useQuery({
    query: CurrentUserAndEnrolment,
    variables: [],
  })

  // State
  // ---------------------------------------------------------------------------

  // Render
  // ---------------------------------------------------------------------------

  const { data, error } = result

  useEffect(() => {
    if (error) {
      displayFetchError(displayError, error)
      // The API will not error if there is no session. It will just return
      // nothing for the currentUser.
    } else if (data) {
      if (hasDataWithNoUser(data)) {
        displayNoCurrentUserError(displayError)
      }
    }
  }, [data, error, displayError, errorReporter])

  if (error || !data || hasDataWithNoUser(data)) {
    return <LoadOverlay />
  }

  const { currentEnrolment } = data

  return (
    <UserContext.Provider value={data}>
      <PageMonitorService
        currentEnrolment={currentEnrolment}
        location={location}
      />
      {children}
    </UserContext.Provider>
  )
}

App.propTypes = {
  children: PropTypes.node.isRequired,
}

export default App
